import {
  useReplayCreationActions,
  type ReplayCreationActionsResponse,
  type ReplayCreationActionsResultItem,
} from "@/api/super-admin/use-replay-creation-actions";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RotateCcw,
  SkipForward,
  XCircle,
} from "lucide-react";
import { useState } from "react";

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: "sent" | "skipped" | "error" }) {
  if (status === "sent") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        envoyé
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
        <XCircle className="h-3.5 w-3.5" />
        erreur
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <SkipForward className="h-3.5 w-3.5" />
      ignoré
    </span>
  );
}

// ─── Summary cards ────────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: "emerald" | "red" | "slate" | "violet";
}) {
  const colorMap = {
    emerald: "text-emerald-700",
    red: "text-red-600",
    slate: "text-slate-600",
    violet: "text-violet-600",
  };
  return (
    <div className="rounded-lg border bg-card px-4 py-3 flex flex-col gap-0.5">
      <span className={`text-2xl font-bold tabular-nums ${colorMap[color]}`}>
        {value}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ─── Results table ────────────────────────────────────────────────────────────

function ResultsTable({
  results,
}: {
  results: ReplayCreationActionsResultItem[];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-muted-foreground">
              ID
            </th>
            <th className="px-4 py-2 text-left font-medium text-muted-foreground">
              Email
            </th>
            <th className="px-4 py-2 text-left font-medium text-muted-foreground">
              Webhooks
            </th>
            <th className="px-4 py-2 text-left font-medium text-muted-foreground">
              Klaviyo
            </th>
            <th className="px-4 py-2 text-left font-medium text-muted-foreground">
              Erreur
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {results.map((r) => (
            <tr key={r.id} className={r.error ? "bg-red-50" : ""}>
              <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                {r.id}
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={r.email} />
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={r.webhooks} />
              </td>
              <td className="px-4 py-2">
                <StatusBadge status={r.klaviyo} />
              </td>
              <td className="px-4 py-2 text-xs text-red-600 font-mono">
                {r.error ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

export function ReplayCreationActionsTab() {
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState<ReplayCreationActionsResponse | null>(
    null,
  );
  const mutation = useReplayCreationActions();

  const ids = raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  function handleRun() {
    if (!ids.length) return;
    setResult(null);
    mutation.mutate(ids, {
      onSuccess: (data) => setResult(data),
    });
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">
          Rejouer les actions de création
        </h2>
        <p className="text-sm text-muted-foreground">
          Re-déclenche les actions manquantes (email, webhooks, Klaviyo) pour
          une liste de return requests. Chaque action est vérifiée
          individuellement dans les logs — aucune action déjà présente ne sera
          rejouée.
        </p>
      </div>

      {/* Input */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          IDs des return requests
          {ids.length > 0 && (
            <span className="ml-2 text-muted-foreground font-normal">
              ({ids.length} détectés)
            </span>
          )}
        </label>
        <textarea
          className="w-full rounded-lg border bg-background px-3 py-2 text-sm font-mono resize-y min-h-[120px] focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent placeholder:text-muted-foreground"
          placeholder={"5RVGANIy02XNTzQAs3\nA9FXFyhiD4qQWVDcNI\n..."}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Un ID par ligne, ou séparés par des virgules.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleRun}
          disabled={!ids.length || mutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4" />
          )}
          {mutation.isPending ? "En cours…" : "Rejouer"}
        </button>
        {result && !mutation.isPending && (
          <button
            onClick={() => {
              setRaw("");
              setResult(null);
              mutation.reset();
            }}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Error */}
      {mutation.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Erreur lors de l&apos;appel :{" "}
            {mutation.error instanceof Error
              ? mutation.error.message
              : "Erreur inconnue"}
          </span>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Résultats</h3>

          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard
              label="Total traités"
              value={result.summary.total}
              color="violet"
            />
            <SummaryCard
              label="Emails envoyés"
              value={result.summary.emailsSent}
              color="emerald"
            />
            <SummaryCard
              label="Webhooks envoyés"
              value={result.summary.webhooksSent}
              color="slate"
            />
            <SummaryCard
              label="Erreurs"
              value={result.summary.errors}
              color={result.summary.errors > 0 ? "red" : "slate"}
            />
          </div>

          <ResultsTable results={result.results} />
        </div>
      )}
    </div>
  );
}
